<template>
  <div :class="[!autoHeight ? 'h-96':'']" :style="{ boxShadow: shadow ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none' }">
    <AgGridVue
      :theme="theme"
      style="width: 100%; height: 100%"
      :column-defs="computedColumnDefs"
      :row-data="rowData"
      :default-col-def="defaultColDef"
      :auto-group-column-def="autoGroupColumnDef"
      :group-display-type="groupDisplayType"
      :grid-options="gridOptions"
      :loading="loading"
      @grid-ready="onGridReady"
      @cellValueChanged="onCellValueChanged"
      :key="tableKey"
    />

    <ConfirmationModal
      :is-visible="isOpenDeleteActionModal"
      :message="'Are you sure you want to delete this action item?'"
      @confirm="deleteAction"
      @cancel="isOpenDeleteActionModal = !isOpenDeleteActionModal"
    />

    <ConfirmationModal
      :is-visible="isOpenCompleteActionModal"
      :message="completeModalMessage"
      @confirm="completeAction"
      @cancel="isOpenCompleteActionModal = !isOpenCompleteActionModal"
    />

  <ConfirmationModal
    :is-visible="isOpenCompleteActionModal"
    :message="completeModalMessage"
    @confirm="completeAction"
    @cancel="isOpenCompleteActionModal = !isOpenCompleteActionModal"
  />

  <Generic_Form
    v-if="isEditModalOpen"
    :model="'action'"
    :object="editActionData"
    :mode="'Edit'"
    :hideIcon="true"
    :isModalOpen="true"
    @update="editAction"
    @close="isEditModalOpen = false"
  />

  <FormUpdateObjectOwner
    v-if="showOwnerModal"
    :object-id="ownerModalData?.objectId"
    :content-type-model="ownerModalData?.objectContentTypeModel"
    :current-owner="ownerModalData?.ownerId"
    @close="onOwnerModalClose"
  />

</div>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import CC_Transcript_Popup from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Transcript_Popup.vue';
import ConfirmationModal from "@/components/widgetcomponents/ConfirmationModal.vue";
import CC_Delete from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Button_Generic_Function.vue";
import CC_Dropdown_Menu from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Dropdown_Menu.vue";
import { parse, format } from 'date-fns';
import CC_Owner_Badge from "@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Owner_Badge.vue";
import { isBefore, startOfDay } from 'date-fns';
import Generic_Form from '@/components/forms/Generic_Form.vue';
import FormUpdateObjectOwner from '@/components/forms/FormUpdateObjectOwner.vue';
import {standardListTheme} from "@/utils/common";
import CC_Router_Link_To_Entity from '../widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue';

export default {
  components: {
    ConfirmationModal,
    AgGridVue,
    CC_Router_Link_To_Entity,
    CC_Transcript_Popup,
    CC_Dropdown_Menu,
    Generic_Form,
    CC_Owner_Badge,
    FormUpdateObjectOwner,
  },
  props: {
    autoHeight:{
      type: Boolean,
      required: false,
      default: false
    },
    // Entity-based filtering (for Overview_Entity)
    entityId: {
      type: Number,
      required: false,
    },
    includeDescendants: {
      type: Boolean,
      required: false,
      default: true
    },
    // Originating object filtering (for Overview_Engagement)
    originatingObjectId: {
      type: [Number, String],
      required: false
    },
    originatingObjectContentTypeModel: {
      type: String,
      required: false
    },
    // Owner filtering (for Overview_User)
    ownerObjectId: {
      type: [Number, String],
      required: false
    },
    ownerContentTypeModel: {
      type: String,
      required: false
    },
    // Completion status filtering
    completedDateIsNull: {
      type: Boolean,
      required: false,
      default: null // null means don't filter by completion status
    },
    // Legacy sqlFilters prop for backward compatibility
    sqlFilters: {
      type: Object,
      required: false,
      default: () => ({})
    },
    theme: {
      type: Object,
      required: false,
      default: standardListTheme
    },
    shadow: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      gridApi: null,
      gridOptions: {
        suppressAggFuncInHeader: true,
        domLayout: this.autoHeight ? 'autoHeight' : null
      },
      loading: true,
      groupDisplayType: 'singleColumn',
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
        autoHeight: true,
        wrapText: true,
      },
      autoGroupColumnDef: {
        headerName: 'Business Unit',
        cellRendererParams: {
          suppressCount: false,
        },
      },
      rowData: [],
      tableKey: 0,
      isOpenDeleteActionModal: false,
      isOpenCompleteActionModal: false,
      completeActionId: null,
      isActionComplete: false,
      deleteActionId: null,
      editActionData: null,
      isEditModalOpen: false,
      showOwnerModal: false,
      ownerModalData: null,
    };
  },
  computed: {
    standardListTheme() {
      return standardListTheme
    },
    computedColumnDefs() {
      return [
        {
          headerName: 'Type',
          headerTooltip: 'Type',
          cellClass: params => {
            const baseClass = 'cell-theme-left-aligned font-semibold';
            const dueDate = new Date(params.data.due_date);
            const today = startOfDay(new Date());

            if (params.data.due_date && !params.data.completed_date && isBefore(dueDate, today)) {
              return `${baseClass} text-red-600`;
            }
            return baseClass;
          },
          field: 'action_name',
          minWidth: 150,
        },
        {
          headerName: 'Description',
          headerTooltip: 'Description',
          field: 'description',
          cellClass: 'cell-theme-left-aligned',
          wrapText: true,
          editable: true,
          minWidth: 300,
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            db_field: 'description',
            var_type: 'text'
          }
        },
        {
          headerName: 'Owner',
          headerTooltip: 'Owner',
          cellClass: 'cell-theme-center-aligned justify-center',
          field: 'owner_name',
          cellRenderer: CC_Owner_Badge,
          cellRendererParams: {
            refreshData: this.refreshData,
            badge_type: 'action_owner',
            getOwnerData: (params) => ({
              ownerId: params.data.owner_object_id,
              ownerType: params.data.owner_content_type_model,
              ownerName: params.data.owner_name,
              ownerColor: params.data.owner_color || '#3B82F6',
              objectContentTypeModel: 'action',
              objectId: params.data.id
            }),
            on: {
              'update-owner': (data) => this.onOwnerModalOpen(data)
            },
          },
        },
        {
          headerName: 'For',
          headerTooltip: 'For',
          cellClass: 'cell-theme-left-aligned font-semibold',
          field: 'related_object_name',
          cellRenderer: CC_Router_Link_To_Entity,
          cellRendererParams: {
            dataType: 'entity',
            idField: 'entity',
            nameField: 'entity_name',
          }
        },
        {
          headerName: 'Due Date',
          headerTooltip: 'Due Date',
          cellClass: params => {
            const dueDate = new Date(params.value);
            const today = startOfDay(new Date());
            const baseClass = 'cell-theme-left-aligned';

            if (params.value && !params.data.completed_date && isBefore(dueDate, today)) {
              return `${baseClass} text-red-600 font-semibold`;
            }
            return baseClass;
          },
          field: 'due_date',
          editable: true,
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            var_type: 'date',
            format: 'YYYY-MM-DD',
            db_field: 'due_date'
          },
          sort: 'desc'
        },
        {
          headerName: 'Completed Date',
          headerTooltip: 'Completed Date',
          cellClass: 'cell-theme-left-aligned',
          field: 'completed_date',
          editable: true,
          cellEditor: 'agTextCellEditor',
          filter: 'agSetColumnFilter',
          cellEditorParams: {
            var_type: 'date',
            format: 'YYYY-MM-DD',
            db_field: 'completed_date'
          }
        },
        {
          headerName: 'Complete',
          cellClass: 'cell-theme-center-aligned justify-center',
          suppressHeaderFilterButton: true,
          cellRenderer: CC_Delete,
          cellRendererParams: {
            function_type: 'complete_action',
            openCompleteActionConfirmationModal: ((actionId, action_completed_date) => {
              this.isOpenCompleteActionModal = true;
              this.completeActionId = actionId;
              this.isActionComplete = !!action_completed_date
            })
          },
        },
        {
          headerName: 'More',
          cellClass: 'cell-theme-center-aligned justify-center',
          maxWidth: 100,
          suppressHeaderFilterButton: true,
          cellRenderer: CC_Dropdown_Menu,
          cellRendererParams: {
            menuItems: [
              {
                label: 'Edit',
                action: (params) => {
                  const transformedData = {
                    ...params.data,
                    id: params.data.id,
                    action_type: params.data.action_type,
                    action_name: params.data.action_name,
                    description: params.data.description,
                    notes: params.data.notes,
                    due_date: params.data.due_date,
                    completed_date: params.data.completed_date,
                  }

                  this.editActionData = transformedData;
                  this.isEditModalOpen = true;
                }
              },
              {
                label: 'Delete',
                action: (params) => {
                  this.isOpenDeleteActionModal = true;
                  this.deleteActionId = params.data.object_id;
                }
              }
            ]
          },
        },
        { field: 'id', hide: true },
        { field: 'owner_object_id', hide: true },
        { field: 'owner_content_type_model', hide: true },
        { field: 'owner_color', hide: true },
        { field: 'created_at', hide: true, sort: 'desc' }
      ];
    },
    completeModalMessage() {
      return this.isActionComplete
        ? 'Mark this action as incomplete?'
        : 'Mark this action as completed?';
    },
    // Build query parameters based on props
    queryParams() {
      const params = {};

      // Entity-based filtering
      if (this.entityId) {
        params.entity_id = this.entityId;
        params.include_descendants = this.includeDescendants;
      }

      // Originating object filtering
      if (this.originatingObjectId && this.originatingObjectContentTypeModel) {
        params.originating_object_id = this.originatingObjectId;
        params.originating_object_content_type_model = this.originatingObjectContentTypeModel;
      }

      // Owner filtering
      if (this.ownerObjectId && this.ownerContentTypeModel) {
        params.owner_object_id = this.ownerObjectId;
        params.owner_content_type_model = this.ownerContentTypeModel;
      }

      // Completion status filtering
      if (this.completedDateIsNull !== null) {
        params.completed_date_isnull = this.completedDateIsNull;
      }

      // Legacy sqlFilters support
      if (this.sqlFilters && Object.keys(this.sqlFilters).length > 0) {
        // Handle legacy filter format
        if (this.sqlFilters.originating_object_id) {
          params.originating_object_id = this.sqlFilters.originating_object_id;
        }
        if (this.sqlFilters.originating_object_content_type_model) {
          params.originating_object_content_type_model = this.sqlFilters.originating_object_content_type_model;
        }
        if (this.sqlFilters.action_owner_object_id) {
          params.owner_object_id = this.sqlFilters.action_owner_object_id;
        }
        if (this.sqlFilters.action_owner_content_type_model) {
          params.owner_content_type_model = this.sqlFilters.action_owner_content_type_model;
        }
        if (this.sqlFilters.action_completed_date?.['IS NULL'] !== undefined) {
          params.completed_date_isnull = true;
        }
        if (this.sqlFilters.action_completed_date?.['IS NOT NULL'] !== undefined) {
          params.completed_date_isnull = false;
        }
      }

      return params;
    }
  },
  methods: {
    async onGridReady(params) {
      this.gridApi = params.api;
      try {
        const response = await axios.get('api/actions/', {
          params: this.queryParams
        });

        if (response.data?.length > 0) {
          this.rowData = response.data;
        } else {
          this.rowData = [];
        }
        this.loading = false;

        params.api.sizeColumnsToFit();
        window.addEventListener('resize', this.onWindowResize);

      } catch (error) {
        console.error('Error fetching data:', error);
        this.rowData = [];
        this.loading = false;
      }
    },

    async refreshData() {
      try {
        const response = await axios.get('api/actions/', {
          params: this.queryParams
        });

        if (Array.isArray(response.data) && response.data.length > 0) {
          this.rowData = response.data;
        } else {
          this.rowData = [];
        }
        this.loading = false;

        if (this.gridApi) {
          this.gridApi.refreshCells();
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.loading = false;
      }
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },

    editAction() {
      this.refreshData();
      this.isEditModalOpen = false;
    },

    async deleteAction() {
      try {
        await axios.delete(`api/actions/${this.deleteActionId}/`);

        this.isOpenDeleteActionModal = false;
        this.deleteActionId = null;
        this.refreshData();
      } catch (error) {
        console.error('Error deleting action', error);
      }
    },

    async completeAction() {
      try {
        const postData = this.isActionComplete ? { incomplete: true } : {};
        await axios.post(`api/actions/${this.completeActionId}/complete/`, postData);

        this.isOpenCompleteActionModal = false;
        this.completeActionId = null;
        this.isActionComplete = false;
        this.refreshData();
      } catch (error) {
        console.error('Error completing action item', error);
      }
    },

    async onCellValueChanged(event) {
      const { data, colDef, newValue } = event;
      const field = colDef.cellEditorParams.db_field;
      let value = newValue;

      if (colDef.cellEditorParams.var_type === 'date') {
        // Function to parse and format date
        const parseAndFormatDate = (input) => {
          const parsedDate = parse(input, 'yyyy-MM-dd', new Date()) ||
                             parse(input, 'MM/dd/yyyy', new Date()) ||
                             parse(input, 'dd-MM-yyyy', new Date());
          if (isNaN(parsedDate)) return null;
          return format(parsedDate, 'yyyy-MM-dd');
        };

        value = parseAndFormatDate(newValue);
        if (!value) {
          alert('Invalid date format.');
          return;
        }
      }

      try {
        await axios.patch(
          `api/actions/${data.id}/`,
          { [field]: value },
        );
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
      }
    },
    onOwnerModalOpen(data) {
      this.ownerModalData = data;
      this.showOwnerModal = true;
    },
    onOwnerModalClose(data) {
      this.showOwnerModal = false;
      if (data.refreshData) {
        this.refreshData();
      }
    }
  },

  watch: {
    // Refresh data if filtering props change
    entityId() {
      if (this.gridApi) this.refreshData();
    },
    originatingObjectId() {
      if (this.gridApi) this.refreshData();
    },
    ownerObjectId() {
      if (this.gridApi) this.refreshData();
    },
    completedDateIsNull() {
      if (this.gridApi) this.refreshData();
    },
    sqlFilters: {
      deep: true,
      handler() {
        if (this.gridApi) this.refreshData();
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }
};
</script>

<style scoped>
.text-right {
  text-align: right;
}

.btn-primary-sm {
  padding: 0.5rem 1rem;
  background-color: #fb923c;
  color: white;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
}

.btn-primary-sm:hover {
  background-color: #f97316;
}
</style>
